<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="a"
                :pages="[
                    {
                        name: $tc('messages.activities', 2),
                        href: meta.links.index,
                    },
                    {
                        name: activity.title,
                        href: meta.links.show,
                    },
                    {
                        name: $t('messages.edit'),
                        href: null,
                    },
                ]"
            />
        </template>

        <UnsavedChangesFormWarning :is-dirty="form.isDirty" v-if="form.isDirty" />
        <form :action="meta.links.update" method="post" @submit.prevent="submit">
            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.information') }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-8">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.title')" for="title" />
                            <FormInput
                                required
                                id="title"
                                v-model="form.title"
                                name="title"
                                type="text"
                                max="191"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.title" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-4">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.trimester')" for="trimester" />
                            <FormSelect
                                required
                                id="trimester"
                                v-model="form.trimester"
                                :options="options.trimesters"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.trimester" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-3">
                        <div class="mt-1">
                            <FormLabel :value="$tc('messages.stages', 1)" for="stage_id" />
                            <FormSelect
                                required
                                id="stage_id"
                                v-model="form.stage_id"
                                :options="options.stages"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.stage_id" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-5">
                        <FormLabel :value="$t('messages.grade_level')" for="grade_level_ids" />
                        <div class="mt-1">
                            <SlideOverCheckboxInput v-model="form.grade_level_ids" :options="filteredGradeLevels" />
                        </div>
                    </div>

                    <div class="sm:col-span-4">
                        <SelectWithPreferences
                            :options="filteredSubjects"
                            name="subject_id"
                            :required="true"
                            :label="$tc('messages.subject', 1)"
                            property-name="text"
                            :preferences="defaults.preferences.subject_ids"
                            :default-option="form.subject_id"
                            property-value="value"
                            v-model="form.subject_id"
                        />
                        <FormError :value="form.errors.subject_id" class="mt-2" />
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.comment') }}
                    <p class="mt-1 text-sm leading-5 text-gray-500">
                        {{ $t('messages.information_only_visible_by_teachers') }}
                    </p>
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-12">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.comment')" for="comment" class="sr-only" />
                            <FormTextarea
                                rows="3"
                                id="comment"
                                v-model="form.comment"
                                name="comment"
                                type="text"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.comment" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.description') }}
                    <p class="mt-1 text-sm leading-5 text-gray-500">
                        {{ $t('messages.information_visible_by_students') }}
                    </p>
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-12">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.description')" for="description" class="sr-only" />
                            <FormTextarea
                                rows="3"
                                id="description"
                                v-model="form.description"
                                name="description"
                                type="text"
                                class="mt-1 alert-background-icon w-full"
                            />
                            <FormError :value="form.errors.description" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.attention_to_diversity') }}
                    <p class="mt-1 text-sm leading-5 text-gray-500">
                        {{ $t('messages.information_only_visible_by_teachers') }}
                    </p>
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-12">
                        <div class="mt-1">
                            <FormLabel
                                :value="$t('messages.attention_to_diversity')"
                                for="attention_to_diversity"
                                class="sr-only"
                            />
                            <FormTextarea
                                rows="3"
                                id="attention_to_diversity"
                                v-model="form.attention_to_diversity"
                                name="attention_to_diversity"
                                type="text"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.attention_to_diversity" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection v-if="activity.is_quiz">
                <template v-slot:title>
                    {{ $t('messages.accompanying_text') }}
                    <p class="mt-1 text-sm leading-5 text-gray-500">
                        {{ $t('messages.information_visible_by_students') }}
                    </p>
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-12">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.accompanying_text')" for="body" />
                            <Editor id="body" v-model="form.accompanying_text" name="body" class="mt-2" />
                            <FormError :value="form.errors.accompanying_text" class="mt-2" />
                        </div>
                    </div>
                    <div class="sm:col-span-12" v-if="form.image_path">
                        <div class="mt-1">
                            <img :src="activity.image_path" alt="cover" class="h-auto" />
                        </div>
                        <div class="mt-1">
                            <AppButton @click="form.image_path = null" element="button" type="button">
                                {{ $tc('messages.delete_photo', 2) }}
                            </AppButton>
                        </div>
                    </div>
                    <div class="sm:col-span-12" v-else>
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.image')" for="image" />
                            <input
                                id="image"
                                @input="form.image = $event.target.files[0]"
                                name="image"
                                type="file"
                                accept="image/png,image/jpg,image/jpeg,"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.image" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>
            <TitleFieldsFormSection v-if="activity.is_quiz">
                <template v-slot:title>
                    {{ $tc('messages.quizzes', 1) }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-3">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.doable_days')" for="doable_days" />
                            <FormInput
                                required
                                v-model="form.doable_days"
                                id="doable_days"
                                name="doable_days"
                                min="1"
                                type="number"
                                max="250"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.doable_days" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-3" v-if="isFixedQuiz">
                        <div class="mt-1">
                            <FormLabel
                                :value="$t('messages.randomize_questions')"
                                for="randomize_questions"
                                class="sr-only"
                            />
                            <FormCheckbox
                                v-model="form.randomize_questions"
                                id="randomize_questions"
                                name="randomize_questions"
                            >
                                {{ $t('messages.randomize_questions') }}
                            </FormCheckbox>
                            <FormError :value="form.errors.randomize_questions" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-3">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.adapted')" for="is_adaptation_active" />
                            <input
                                id="is_adaptation_active"
                                type="checkbox"
                                v-model="form.is_adaptation_active"
                                name="is_adaptation_active"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                            />
                            <FormError :value="form.errors.is_adaptation_active" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection v-if="isFixedQuiz">
                <template v-slot:title>
                    <div>
                        {{ $t('messages.quiz_reviewable') }}
                    </div>
                    <div>
                        <QuizFormReviewExplanation
                            :is-reviewable="form.is_reviewable"
                            :is-reviewed-with-details="form.is_reviewed_with_details"
                        />
                    </div>
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-4">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.is_reviewable')" for="is_reviewable" class="sr-only" />
                            <FormCheckbox v-model="form.is_reviewable" id="is_reviewable" name="is_reviewable">
                                {{ $t('messages.is_reviewable') }}
                            </FormCheckbox>
                            <FormError :value="form.errors.is_reviewable" class="mt-2" />
                        </div>
                    </div>
                    <div
                        class="sm:col-span-8"
                        :class="{
                            'opacity-50': !form.is_reviewable,
                        }"
                    >
                        <div class="mt-1">
                            <FormLabel
                                :value="$t('messages.is_reviewed_with_details')"
                                for="is_reviewed_with_details"
                                class="sr-only"
                            />
                            <FormCheckbox
                                v-model="form.is_reviewed_with_details"
                                id="is_reviewed_with_details"
                                name="is_reviewed_with_details"
                            >
                                {{ $t('messages.is_reviewed_with_details') }}
                            </FormCheckbox>
                            <FormError :value="form.errors.is_reviewed_with_details" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection v-show="form.weight > 0">
                <template v-slot:title>
                    {{ $t('messages.score') }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.max_score')" for="max_score" />
                            <div class="mt-1" v-if="activity.is_quiz">
                                {{ activity.max_score_display }}
                            </div>
                            <FormInput
                                v-else
                                required
                                id="max_score"
                                v-model="form.max_score"
                                name="max_score"
                                type="number"
                                step="1"
                                class="mt-1"
                            />
                            <FormError :value="form.errors.max_score" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $tc('messages.materials', 2) }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-12">
                        <MaterialBatchCreate v-model="form.materials" :errors="form.errors" />
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.advanced_options') }}
                </template>

                <SeeMore>
                    <template v-slot:content>
                        <div class="md:grid md:grid-cols-12 md:gap-6">
                            <div class="sm:col-span-3">
                                <div class="mt-1">
                                    <FormLabel :value="$t('messages.score_weight')" for="weight" />
                                    <FormSelect
                                        required
                                        id="weight"
                                        v-model="form.weight"
                                        :options="weightOptions"
                                        class="mt-1 w-full"
                                        :null-option="false"
                                    />
                                    <FormError :value="form.errors.weight" class="mt-2" />
                                </div>
                            </div>

                            <div class="sm:col-span-3">
                                <div class="mt-1">
                                    <FormLabel :value="$t('messages.activity_mode')" for="mode" />
                                    <FormSelect
                                        required
                                        id="mode"
                                        v-model="form.mode"
                                        :options="options.modes"
                                        class="mt-1 w-full"
                                        :null-option="false"
                                    />
                                    <FormError :value="form.errors.mode" class="mt-2" />
                                </div>
                            </div>

                            <div class="sm:col-span-3">
                                <div class="mt-1">
                                    <FormLabel :value="$t('messages.visibility')" for="visibility" />
                                    <FormSelect
                                        required
                                        id="visibility"
                                        v-model="form.visibility"
                                        :options="options.visibilities"
                                        class="mt-1 w-full"
                                    />
                                    <FormError :value="form.errors.visibility" class="mt-2" />
                                </div>
                            </div>

                            <div class="sm:col-span-3">
                                <div class="mt-1">
                                    <FormLabel :value="$t('messages.grouping')" for="grouping" />
                                    <FormSelect
                                        id="grouping"
                                        v-model="form.grouping"
                                        :options="options.groupings"
                                        class="mt-1 w-full"
                                    />
                                    <FormError :value="form.errors.grouping" class="mt-2" />
                                </div>
                            </div>

                            <div class="sm:col-span-3">
                                <div class="mt-1">
                                    <FormLabel :value="$t('messages.duration')" for="duration" />
                                    <FormInput
                                        id="duration"
                                        v-model="form.duration"
                                        name="duration"
                                        type="number"
                                        step="1"
                                        class="mt-1 w-full"
                                        list="minutes"
                                    />
                                    <datalist id="minutes">
                                        <option value="15"></option>
                                        <option value="30"></option>
                                        <option value="45"></option>
                                    </datalist>
                                    <FormError :value="form.errors.duration" class="mt-2" />
                                </div>
                            </div>
                        </div>
                    </template>
                </SeeMore>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.gamification_options') }}
                </template>

                <FormCheckbox v-model="form.gives_gamification_points">
                    {{ $t('messages.gives_gamification_points') }}
                </FormCheckbox>
            </TitleFieldsFormSection>

            <div class="flex justify-end mt-5 space-x-2">
                <FormCheckbox v-model="form.draft">
                    {{ $t('messages.draft') }}
                </FormCheckbox>

                <AppButton desired-action element="button" type="submit" :disabled="form.processing">
                    {{ $t('messages.save') }}
                    <span v-if="form.processing" class="ml-1 btn-spinner"></span>
                </AppButton>
            </div>
        </form>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import TitleFieldsFormSection from '@/components/TitleFieldsFormSection.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import FormTextarea from '@/components/Forms/FormTextarea.vue';
import FormCheckbox from '@/components/Forms/FormCheckbox.vue';
import MaterialBatchCreate from '@/components/pageSpecific/MaterialBatchCreate.vue';
import Editor from '@/components/Forms/RichTextEditor/Editor.vue';
import AppButton from '@/components/AppButton.vue';
import SelectWithPreferences from '@/components/SelectWithPreferences.vue';
import SlideOverCheckboxInput from '@/components/Forms/SlideOverCheckboxInput.vue';
import UnsavedChangesFormWarning from '@/components/Forms/UnsavedChangesFormWarning.vue';
import SeeMore from '../../../components/SeeMore.vue';
import QuizFormReviewExplanation from './partials/QuizFormReviewExplanation.vue';

export default {
    components: {
        QuizFormReviewExplanation,
        SeeMore,
        UnsavedChangesFormWarning,
        FormCheckbox,
        FormTextarea,
        PanelPage,
        TitleFieldsFormSection,
        FormInput,
        FormLabel,
        FormError,
        FormSelect,
        MaterialBatchCreate,
        AppButton,
        Editor,
        SelectWithPreferences,
        SlideOverCheckboxInput,
    },
    layout: Layout,
    name: 'Edit',
    props: ['teacher', 'meta', 'options', 'activity', 'defaults'],
    computed: {
        filteredGradeLevels: function () {
            return this.options.grade_levels.filter((s) => s.stage_id == this.form.stage_id);
        },
        filteredSubjects: function () {
            return this.options.subjects.filter((s) => s.stage_id == this.form.stage_id);
        },
        weightOptions: function () {
            return this.options.weights;
        },
        isFixedQuiz() {
            return this.activity.type === 'fixed-quiz';
        },
    },
    watch: {
        'form.stage_id': function (newVal, oldVal) {
            if (newVal !== oldVal) {
                this.form.grade_level_ids = [];
                this.form.subject_id = null;
            }
        },
    },
    data() {
        return {
            form: this.$inertia.form({
                _method: 'patch',
                title: this.activity.title,
                draft: this.activity.draft,
                subject_id: this.activity.subject_id,
                max_score: this.activity.max_score,
                trimester: this.activity.trimester,
                comment: this.activity.comment,
                description: this.activity.description,
                accompanying_text: this.activity.accompanying_text,
                attention_to_diversity: this.activity.attention_to_diversity,
                stage_id: this.activity.stage_id,
                grade_level_ids: this.activity.grade_level_ids.map((v) => +v),
                weight: this.activity.weight,
                visibility: this.activity.visibility,
                grouping: this.activity.grouping,
                duration: this.activity.duration,
                materials: this.activity.materials,
                //image
                image: null,
                image_path: this.activity.image_path,
                //quiz
                doable_days: this.activity.doable_days,
                randomize_questions: this.activity.randomize_questions,
                is_adaptation_active: this.activity.is_adaptation_active,
                gives_gamification_points: this.activity.gives_gamification_points,
                is_reviewable: this.activity.is_reviewable,
                is_reviewed_with_details: this.activity.is_reviewed_with_details,
                mode: this.activity.mode,
            }),
        };
    },
    methods: {
        submit() {
            this.form.post(this.meta.links.update, {
                onSuccess: () => this.form.reset('image_path'),
            });
        },
    },
};
</script>
